import { produce } from 'immer';
import { isDeepEqual } from 'remeda';
import type { Editor, NodeEntry } from 'slate';
import { Path } from 'slate';

import { assertNeverSafely, formatPageNumber } from '../../util';
import type {
  CacheableCMNode,
  CacheableRMNodeFor,
  CachedRMNodeInfoFor,
  FooterElement,
  FootersElement,
  HeaderElement,
  HeadersElement,
  InlineInstructionFieldVariant,
  PageNumberingConfig,
  ParagraphElement,
  RenderModelNodeWithRelPath,
  RenderModelPage,
} from '../internal';
import {
  addRMNodeToCache,
  FooterType,
  HeaderType,
  isInlineInstructionFieldElement,
  Queries,
} from '../internal';

/**
 * Ensure that metadata such as containerWidthPx matches the cached node. If the metadata does not
 * match, return null to indicate that the render model node should be recomputed.
 *
 * In addition, this function ensures that the relativePath of the cached node is correct. If the
 * relativePath does not match, cache and return a version of the cached node with relativePath
 * updated.
 */
export function validateRMNodeCache<T extends CacheableCMNode>({
  cMNodeEntry,
  cachedNodeInfo,
  containerWidthPx,
  isUniqueInHeaderFooter,
  pageNum,
}: {
  cachedNodeInfo: CachedRMNodeInfoFor<T>;
  cMNodeEntry: NodeEntry<T>;
  containerWidthPx: number;
  isUniqueInHeaderFooter: boolean;
  pageNum: number;
}): CacheableRMNodeFor<T> | null {
  const [cMNode, cMNodePath] = cMNodeEntry;
  const { cachedNode: rMNode, ...metadata } = cachedNodeInfo;
  const expectedMetadata: typeof metadata = { containerWidthPx };
  if (!isDeepEqual(metadata, expectedMetadata)) return null;

  const relativePath = Queries.getPathRelativeToParent(cMNodePath);
  const updatedNode = validateRMNodePath(rMNode, relativePath);

  if (updatedNode === rMNode) return rMNode;

  addRMNodeToCache({
    cMNode,
    containerWidthPx,
    isUniqueInHeaderFooter,
    pageNum,
    rMNode: updatedNode,
  });

  return updatedNode;
}

/**
 * Makes sure that rMNode.relativePath is correct.
 */
export function validateRMNodePath<T extends RenderModelNodeWithRelPath>(
  node: T,
  relativePath: Path
): T {
  if (Path.equals(node.relativePath, relativePath)) return node;

  return { ...node, relativePath };
}

export function initNewRMPage(
  rMPage: RenderModelPage,
  options: {
    shouldIncrementPageNum: boolean;
  }
): RenderModelPage {
  return {
    ...rMPage,
    children: [],
    pageNum: rMPage.pageNum + (options.shouldIncrementPageNum ? 1 : 0),
  };
}

/**
 * Processes instruction field elements in a paragraph by replacing their text content
 * with appropriate values based on the instruction type.
 *
 * @param editor - The editor instance.
 * @param cMPara - The paragraph element to process
 * @param pageNum - The current page number
 * @returns A new paragraph with processed instruction fields
 */
export function processInstructionFields(
  editor: Editor,
  cMPara: ParagraphElement,
  pageNum: number
): ParagraphElement {
  const hasInstructionFields = cMPara.children.some(isInlineInstructionFieldElement);
  if (!hasInstructionFields || !editor.renderModel) return cMPara;

  const totalPages = editor.renderModel.children.length;
  const pageNumbering = Queries.getActivePageNumbering(editor.renderModel);

  // Only create new paragraph if we actually have instruction fields to process.
  return produce(cMPara, draft => {
    for (const child of draft.children) {
      if (isInlineInstructionFieldElement(child)) {
        child.children[0].text = getInstructionFieldText(child.instruction, {
          pageNum,
          pageNumbering,
          totalPages,
        });
      }
    }
  });
}

/**
 * Gets the appropriate text value for an instruction field based on its type.
 *
 * @param instruction - The instruction field variant
 * @param options - Options containing page information
 * @returns The text value to display for the instruction field
 */
function getInstructionFieldText(
  instruction: InlineInstructionFieldVariant,
  options: {
    pageNum: number;
    totalPages: number;
    pageNumbering?: PageNumberingConfig;
  }
): string {
  const { pageNum, pageNumbering, totalPages } = options;

  switch (instruction) {
    case 'PAGE':
      return formatPageNumber(pageNum, pageNumbering);
    case 'NUMPAGES':
      return totalPages.toString();
    case 'REF':
    case 'PAGEREF':
    case 'NOTEREF':
      return '';
    default:
      assertNeverSafely(instruction, `Unsupported instruction field variant: ${instruction}.`);
      return '';
  }
}

export type CMPageHeadersAndFooters = {
  defaultFooter: NodeEntry<FooterElement>;
  defaultHeader: NodeEntry<HeaderElement>;
  evenFooter: NodeEntry<FooterElement>;
  evenHeader: NodeEntry<HeaderElement>;
  firstPageFooter: NodeEntry<FooterElement>;
  firstPageHeader: NodeEntry<HeaderElement>;
};

export function getCMPageHeadersAndFooters(
  editor: Editor,
  headersEntry: NodeEntry<HeadersElement>,
  footersEntry: NodeEntry<FootersElement>
): CMPageHeadersAndFooters {
  const firstPageHeader = Queries.getHeaderOrFooter<'header'>({
    editor,
    partEntry: headersEntry,
    type: HeaderType.FirstPage,
  });
  const evenHeader = Queries.getHeaderOrFooter<'header'>({
    editor,
    partEntry: headersEntry,
    type: HeaderType.Even,
  });
  const defaultHeader = Queries.getHeaderOrFooter<'header'>({
    editor,
    partEntry: headersEntry,
    type: HeaderType.Default,
  });
  const firstPageFooter = Queries.getHeaderOrFooter<'footer'>({
    editor,
    partEntry: footersEntry,
    type: FooterType.FirstPage,
  });
  const evenFooter = Queries.getHeaderOrFooter<'footer'>({
    editor,
    partEntry: footersEntry,
    type: FooterType.Even,
  });
  const defaultFooter = Queries.getHeaderOrFooter<'footer'>({
    editor,
    partEntry: footersEntry,
    type: FooterType.Default,
  });

  return { defaultFooter, defaultHeader, evenFooter, evenHeader, firstPageFooter, firstPageHeader };
}
